/**
 * Unassigned Worklist Card
 *
 * Shows unassigned studies for admin/technician dashboard
 */

import { Link } from 'react-router-dom'
import { Inbox, Loader2, UserPlus, AlertCircle } from 'lucide-react'
import { useUnassignedItems } from '@/application/worklist'
import { useStudy } from '@/application/studies'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { PRIORITY_LABELS, type PriorityLevel } from '@/config/constants'

function UnassignedItem({ studyId, priority }: { studyId: string; priority: number }) {
  const { data: study } = useStudy(studyId)

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--background-subtle)]">
      <div className="flex items-center gap-3">
        <Badge
          variant={
            priority === 1 ? 'destructive' : priority === 2 ? 'warning' : 'secondary'
          }
        >
          {PRIORITY_LABELS[priority as PriorityLevel]}
        </Badge>
        <div>
          <p className="text-sm font-medium">
            {study?.studyDescription || 'Cargando...'}
          </p>
          <p className="text-xs text-[var(--foreground-muted)]">
            {study?.modality} - {study ? new Date(study.studyDate).toLocaleDateString('es-CO') : ''}
          </p>
        </div>
      </div>
      <Link to="/worklist?tab=unassigned">
        <Button variant="outline" size="sm">
          <UserPlus className="h-4 w-4 mr-1" />
          Asignar
        </Button>
      </Link>
    </div>
  )
}

export function UnassignedWorklistCard() {
  const { data: items, isLoading, isError } = useUnassignedItems()

  const unassignedCount = items?.length || 0

  return (
    <Card className={unassignedCount > 0 ? 'border-[var(--color-warning)]/50' : ''}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Inbox className="h-5 w-5" />
          Estudios Sin Asignar
          {unassignedCount > 0 && (
            <Badge variant="warning">{unassignedCount}</Badge>
          )}
        </CardTitle>
        <Link to="/worklist?tab=unassigned">
          <Button variant="ghost" size="sm">
            Ver todos
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center gap-2 py-4 text-red-500">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">Error al cargar</span>
          </div>
        ) : unassignedCount === 0 ? (
          <p className="text-sm text-[var(--foreground-muted)] text-center py-4">
            Todos los estudios estan asignados
          </p>
        ) : (
          <div className="space-y-3">
            {items?.slice(0, 5).map((item) => (
              <UnassignedItem
                key={item.id}
                studyId={item.studyId}
                priority={item.priority}
              />
            ))}
            {unassignedCount > 5 && (
              <p className="text-xs text-[var(--foreground-muted)] text-center">
                +{unassignedCount - 5} mas estudios sin asignar
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
